/**
 * Shared user-identity resolution.
 *
 * The company moved from creyentetech.com to creyenteinfotech.com, and every employee
 * now has two Cognito accounts — one per domain, each with its own Azure AD identity
 * and its own sub. Per-user rows written before the move carry the old-domain email as
 * their key; rows written after carry the new one.
 *
 * A lookup keyed on whichever address the current session happens to report therefore
 * finds only half a person's history, and returns a clean empty page for the other
 * half — which the UI renders identically to "you have no expenses". No error, nothing
 * to notice. That is what made this invisible until someone whose whole history sat on
 * the far side of the move reported it.
 *
 * The rule this module encodes:
 *   reads widen across every alias domain, writes narrow to the canonical one.
 *
 * Widening is safe because both read paths already deduplicate by row id before
 * rendering, and because no local part belongs to two different people across the two
 * domains — `alice@` on one is the same person as `alice@` on the other. If that ever
 * stops being true (a role mailbox reassigned, say), this aliasing would show one
 * person another's rows, so it is the assumption to re-check before adding a domain.
 *
 * Both domain lists come from the environment so that the next domain change is a
 * config edit rather than a hunt through 24 services.
 */

const DEFAULT_CANONICAL_DOMAIN = 'creyenteinfotech.com';
const DEFAULT_DOMAIN_ALIASES = 'creyentetech.com,creyenteinfotech.com';

const parseDomainList = (raw: string): string[] =>
  raw
    .split(',')
    .map(domain => domain.trim().toLowerCase())
    .filter(Boolean);

/** The domain every newly written identity is normalised to. */
export const CANONICAL_EMAIL_DOMAIN = (
  process.env.REACT_APP_CANONICAL_EMAIL_DOMAIN || DEFAULT_CANONICAL_DOMAIN
)
  .trim()
  .toLowerCase();

/**
 * Domains that address the same person interchangeably.
 *
 * The canonical domain is always a member, even if it was left out of the env value —
 * otherwise writes would produce an address that reads never look for.
 */
export const EMAIL_DOMAIN_ALIASES = Array.from(
  new Set([
    ...parseDomainList(process.env.REACT_APP_EMAIL_DOMAIN_ALIASES || DEFAULT_DOMAIN_ALIASES),
    CANONICAL_EMAIL_DOMAIN,
  ])
);

interface EmailParts {
  local: string;
  domain: string;
}

/**
 * Split a value into local part and domain, lowercased, or null if it is not an email.
 *
 * Subs, `azuread_…` usernames and employee ids all land here too, and all of them must
 * come back null so callers can pass them through untouched.
 */
const splitEmail = (value?: string | null): EmailParts | null => {
  const trimmed = (value || '').trim().toLowerCase();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return null;
  return { local: trimmed.slice(0, at), domain: trimmed.slice(at + 1) };
};

/** Whether a value looks like an email address at all. */
export const isEmailAddress = (value?: string | null): boolean => splitEmail(value) !== null;

/**
 * Every address that reaches this person, one per alias domain.
 *
 * An address on a domain we do not alias — a vendor, a personal account — is returned
 * as itself. Expanding it would invent addresses that belong to nobody.
 */
export const emailDomainVariants = (value?: string | null): string[] => {
  const parts = splitEmail(value);
  if (!parts) return [];

  const self = `${parts.local}@${parts.domain}`;
  if (!EMAIL_DOMAIN_ALIASES.includes(parts.domain)) return [self];

  return EMAIL_DOMAIN_ALIASES.map(domain => `${parts.local}@${domain}`);
};

/**
 * The single address to store when writing a new row.
 *
 * Anything that is not an org email is returned trimmed and otherwise unchanged, so
 * this is safe to wrap around a value that might be a sub or might be absent.
 */
export const canonicalizeEmail = (value?: string | null): string => {
  const parts = splitEmail(value);
  if (!parts) return (value || '').trim();

  if (!EMAIL_DOMAIN_ALIASES.includes(parts.domain)) return `${parts.local}@${parts.domain}`;
  return `${parts.local}@${CANONICAL_EMAIL_DOMAIN}`;
};

/**
 * Read-side expansion of any one identity string.
 *
 * Emails on an alias domain fan out to every domain; everything else passes through as
 * itself. Callers can therefore hand this each candidate they hold without first
 * working out which kind of identifier it is.
 */
export const identityVariants = (value?: string | null): string[] => {
  const trimmed = (value || '').trim();
  if (!trimmed) return [];

  const variants = emailDomainVariants(trimmed);
  return variants.length > 0 ? variants : [trimmed];
};

/**
 * Whether two identity strings denote the same person.
 *
 * Replaces `a.toLowerCase() === b.toLowerCase()` wherever the two sides may have been
 * captured under different domains — a row stored against the old address versus a
 * session reporting the new one. Emails are compared canonicalised (so domain and case
 * stop mattering); anything else is compared verbatim, because a sub or an
 * `azuread_…` username differing by case is genuinely a different identifier.
 *
 * Empty on either side is never a match: two users with no email must not collapse
 * into one.
 */
export const sameIdentity = (a?: string | null, b?: string | null): boolean => {
  const left = canonicalizeEmail(a);
  const right = canonicalizeEmail(b);
  if (!left || !right) return false;
  return left === right;
};

/**
 * The row belonging to `identity` in a **raw** Cognito user list, preferring one that actually
 * carries a `managerEmail`.
 *
 * Raw lists — straight from CognitoAdminService.listUsers, before loadAllUsers merges anything —
 * hold one row per account, so a person appears twice: their native row and their federated
 * `azuread_…` one. Callers here all want the same thing from it, the reporting line.
 *
 * Two bugs are avoided by going through this rather than an inline find:
 *
 *  1. A lowercased `===` matched only the row whose address happened to equal the signed-in one,
 *     so somebody signed in on their other domain resolved nothing and lost their manager.
 *  2. Switching that to sameIdentity alone then matches *either* row — and `.find` takes the
 *     first, which may be the federated one that has no managerEmail set. That would have
 *     traded a miss for a silent empty value, which is harder to notice.
 *
 * So: match on identity, then prefer a row that answers the question being asked.
 */
export const findByIdentityWithManager = <T extends { email?: string; managerEmail?: string }>(
  rows: T[] | undefined | null,
  identity?: string | null
): T | undefined => {
  const matches = (rows || []).filter(row => sameIdentity(row.email, identity));
  return matches.find(row => !!row.managerEmail) || matches[0];
};

/**
 * Whether a value actually names somebody.
 *
 * This exists to separate two things that `if (email)` collapses into one, and the collapse
 * was a live data leak. Per-employee cache readers were written as
 *
 *   getRecords(employeeEmail?) { … if (employeeEmail) return filtered; return all; }
 *
 * so the *absent* argument means "every row, for a report or an approver queue". But an
 * argument that is present and empty — `user.email` on an account Cognito has no email
 * attribute for, which `getCurrentUser` resolves to `''` — is a **failed lookup**, and it
 * took the same branch. The filter switched itself off and the caller was handed the whole
 * company's rows as if they were one person's. On the Comp-Off page that showed the super
 * admin a balance made of somebody else's approved day.
 *
 * So: `undefined` still means unscoped, and a scoped read with nothing to scope by returns
 * nothing. Empty is never a wildcard.
 */
export const isResolvedIdentity = (value?: string | null): boolean =>
  (value || '').trim().length > 0;

/** The shape any signed-in user object satisfies, for actor resolution below. */
export interface ActorLike {
  email?: string;
  id?: string;
  name?: string;
}

/**
 * Who to record as having performed an action.
 *
 * Federated Azure AD SAML accounts can arrive with **no email attribute at all** — only an
 * opaque `azuread_…` username in `id`. Code that reached straight for `user.email` therefore
 * had two failure modes, both seen in the Leave module: an audit trail that recorded an empty
 * actor and rendered as "—", and worse, guards written as `if (!user?.email) return;` that
 * silently did nothing, so an HR Admin could click Approve and have no button, no error and no
 * effect. That is why this prefers the email but never resolves to nothing when one exists in
 * any form.
 *
 * Returns '' only when the user is genuinely unidentifiable, which callers should treat as an
 * error to surface rather than a reason to bail out quietly.
 */
export const resolveActorId = (user?: ActorLike | null): string =>
  (user?.email || '').trim() || (user?.id || '').trim();

/**
 * Run a list query once per identity variant and merge what comes back.
 *
 * Queries run concurrently, and one failing candidate does not lose the others — a
 * partial result beats an empty page. Rows are deduplicated by `keyOf` (defaults to
 * `id`) because the same record can legitimately match more than one variant.
 *
 * Pass the identity a caller already holds; it does not need to know whether that is
 * an email, a sub or a username.
 */
export async function collectAcrossIdentities<T>(
  identity: string | null | undefined,
  run: (candidate: string) => Promise<T[]>,
  keyOf: (item: T) => string | undefined = (item: any) => item?.id
): Promise<T[]> {
  const candidates = identityVariants(identity);
  if (candidates.length === 0) return [];
  if (candidates.length === 1) return run(candidates[0]);

  const pages = await Promise.all(
    candidates.map(async candidate => {
      try {
        return await run(candidate);
      } catch (error: any) {
        console.warn(
          `[identity] query failed for "${candidate}":`,
          error?.errors?.[0]?.message || error?.message || error
        );
        return [] as T[];
      }
    })
  );

  const seen = new Set<string>();
  const merged: T[] = [];
  for (const row of pages.flat()) {
    const key = keyOf(row);
    // No key to dedupe on — keep it rather than silently dropping a real row.
    if (key === undefined || key === null || key === '') {
      merged.push(row);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }

  return merged;
}
